// Pure types + validator for the itinerary-detail page's `view`
// search-param. Lives in `model/` so the router (which only needs
// the validator) can import it without dragging in the widget's
// JSX or tRPC.

import type { PlannerSourceSearch } from '@itineraries/features/planner-source-filters';
import type { RemovedServiceReason } from '@travel-planner/shared/schemas/itinerary';
import { validatePlannerSourceSearch } from '@itineraries/features/planner-source-filters';
import { REMOVED_SERVICE_REASONS } from '@travel-planner/shared/schemas/itinerary';

export const ITINERARY_VIEWS = [
  'chapters',
  'calendar',
  'accommodation',
  'activities',
  'dining',
  'logistics',
] as const;

export type ItineraryView = (typeof ITINERARY_VIEWS)[number];

// Workspace mode. Orthogonal to planner `view` (chapters/calendar/…).
//   planner           — consultant planner (default when `mode` absent)
//   traveller         — read-only traveller projection (preview widgets)
//   customer-preview  — Customer Preview collab (`?mode=customer-preview`)
//   copilot           — AI Co-pilot (consultant ↔ LLM)
// Domain: Customer = B2B partner; Traveller = end guest.
// Note: public Traveller Preview URL still accepts `?view=customer` as a
// permanent alias (see apps/preview use-view-mode / routes/preview.ts).
// Workspace `?mode=` has no legacy aliases — only the values above.
// The sold-history builder is NOT a workspace mode; it lives only at
// `/itinerary-builder-eval` (unlisted, no tab / list button).
export const ITINERARY_WORKSPACE_MODES = [
  'planner',
  'traveller',
  'customer-preview',
  'copilot',
] as const;

export type ItineraryWorkspaceMode = (typeof ITINERARY_WORKSPACE_MODES)[number];

/** Customer Preview collab (Accept/Reject + human discussion). */
export const isCustomerPreviewMode = (mode: ItineraryWorkspaceMode): boolean =>
  mode === 'customer-preview';

/** Consultant ↔ LLM Co-pilot workspace (docked Trip Co-pilot rail). */
export const isCopilotMode = (mode: ItineraryWorkspaceMode): boolean =>
  mode === 'copilot';

// `ItineraryDetailSearch` carries the page-level URL params plus the
// planner right-pane filter namespace (`source*`). Spreading
// `PlannerSourceSearch` keeps the validator in one place — Phase 21c
// adds the planner picker as a URL-driven feature, and the source*
// keys never collide with the global /products page's filter names.
export type ItineraryDetailSearch = {
  view?: ItineraryView;
  // Workspace mode. Absent ⇒ planner.
  mode?: ItineraryWorkspaceMode;
  // Active chapter filter (Phase 19a #78). When set, the day list is
  // narrowed to days whose date sits inside the matching chapter's
  // [startDate, endDate] window. Cleared by selecting "All chapters".
  chapter?: string;
  // Active single-day filter (the "View" day selector). When set, the planner
  // (day list + calendar) shows only the day at this index; cleared by
  // selecting "All days".
  day?: number;
  // Active booking-status filter (XT-750) — operation-status ids, plus the
  // `none` sentinel for items carrying no status. Unlike `chapter` / `day`
  // this filter never removes anything: matching rows are highlighted and the
  // rest are dimmed, so drag-and-drop and the budget keep their full day list.
  // Empty / absent means "no filter".
  bookingStatus?: string[];
  // Removed services to show on each day, greyed and read-only: `deleted`
  // (taken off in the planner) and/or `rejected` (by the client or agent).
  // Absent means live services only.
  removed?: RemovedServiceReason[];
  // One-shot signal from the inbox "Start build the trip" action: when
  // `'auto'`, the Co-pilot kicks off a build from the client conversation on
  // open. The page consumes it (drops the param) so a reload doesn't re-fire.
  build?: 'auto';
  // One-shot signal from the item edit page: on returning to the planner, scroll
  // this item into view, then drop the param (XT-788). Without it the planner
  // reopened at the top — Day 1 — after every edit.
  focusItem?: string;
  // One-shot signal from the itineraries list's comments link: Customer
  // Preview scrolls to the first comment awaiting a reply, then drops it.
  focus?: 'pending-comment';
  // Historical (non-active) version being viewed READ-ONLY. Absent ⇒ the live
  // active version, fully editable. Orthogonal to `mode` — it selects which
  // version the planner reads, not which workspace renders.
  //
  // Id-based rather than an ordinal on purpose: `versionTitle` is renameable
  // free text, and a `createdOn` ordinal shifts as versions are created, so
  // neither is a stable key for a shareable/bookmarkable URL.
  versionId?: string;
} & PlannerSourceSearch;

// URL values arrive as strings; coerce to a non-negative day index or undefined.
const asDayIndex = (v: unknown): number | undefined => {
  const n =
    typeof v === 'number'
      ? v
      : typeof v === 'string' && v !== ''
        ? Number(v)
        : Number.NaN;
  return Number.isInteger(n) && n >= 0 ? n : undefined;
};

// Multi-select, so the URL carries a comma-joined list (`?bookingStatus=a,b`).
// Blank entries are dropped and duplicates collapsed; an empty result degrades
// to `undefined` so the param disappears rather than sitting as an empty
// string.
const asBookingStatus = (v: unknown): string[] | undefined => {
  const raw = Array.isArray(v)
    ? v.flatMap((entry) => (typeof entry === 'string' ? entry.split(',') : []))
    : typeof v === 'string'
      ? v.split(',')
      : [];
  const ids = [...new Set(raw.map((s) => s.trim()).filter(Boolean))];
  return ids.length > 0 ? ids : undefined;
};

// `?removed=deleted,rejected` — unknown values are dropped, empty degrades to
// absent.
const asRemoved = (v: unknown): RemovedServiceReason[] | undefined => {
  const raw = Array.isArray(v)
    ? v.flatMap((entry) => (typeof entry === 'string' ? entry.split(',') : []))
    : typeof v === 'string'
      ? v.split(',')
      : [];
  const picked = REMOVED_SERVICE_REASONS.filter((reason) =>
    raw.some((entry) => entry.trim() === reason)
  );
  return picked.length > 0 ? picked : undefined;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A malformed id (`versionId`, `focusItem`) degrades to absent — for a version,
// the live one — rather than throwing a route error, same defensive convention
// as `asDayIndex`.
const asUuid = (v: unknown): string | undefined =>
  typeof v === 'string' && UUID_RE.test(v) ? v : undefined;

export const validateItineraryDetailSearch = (
  input: Record<string, unknown>
): ItineraryDetailSearch => ({
  view:
    typeof input.view === 'string' &&
    (ITINERARY_VIEWS as ReadonlyArray<string>).includes(input.view)
      ? (input.view as ItineraryView)
      : undefined,
  mode:
    typeof input.mode === 'string' &&
    (ITINERARY_WORKSPACE_MODES as ReadonlyArray<string>).includes(input.mode)
      ? (input.mode as ItineraryWorkspaceMode)
      : undefined,
  chapter: typeof input.chapter === 'string' ? input.chapter : undefined,
  day: asDayIndex(input.day),
  bookingStatus: asBookingStatus(input.bookingStatus),
  removed: asRemoved(input.removed),
  build: input.build === 'auto' ? 'auto' : undefined,
  focusItem: asUuid(input.focusItem),
  focus: input.focus === 'pending-comment' ? 'pending-comment' : undefined,
  versionId: asUuid(input.versionId),
  ...validatePlannerSourceSearch(input),
});
